import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { MessageSquare } from 'lucide-react';
import { Card } from '@atoms/Card';
import { useAuthStore } from '@store/authStore';
import { messagingService } from '@services/messagingService';
import { INBOX_QUERY_KEY } from '@utils/messagingCache';
import { ChatInbox } from './ChatInbox';
import { ChatThread } from './ChatThread';
import type { InboxItem } from '@/types';

interface MessagesSectionProps {
  basePath: '/wholesaler/messages' | '/restaurant/messages';
}

export function MessagesSection({ basePath }: MessagesSectionProps) {
  const navigate = useNavigate();
  const { partnerId: routePartnerId } = useParams();
  const user = useAuthStore((s) => s.user);

  const [selected, setSelected] = useState<InboxItem | null>(null);

  const { data: inbox } = useQuery({
    queryKey: INBOX_QUERY_KEY,
    queryFn: () => messagingService.getInbox(),
    staleTime: 15_000,
  });

  useEffect(() => {
    if (!routePartnerId) return;
    const fromInbox = inbox?.find((item) => item.partnerId === routePartnerId);
    if (fromInbox) {
      setSelected(fromInbox);
      return;
    }
    setSelected({
      threadKey: `partner:${routePartnerId}`,
      partnerId: routePartnerId,
      partner: {
        id: routePartnerId,
        name: 'Sohbet',
        role: user?.role === 'wholesaler' ? 'restaurant' : 'wholesaler',
      },
      lastMessage: {
        id: '',
        senderId: '',
        receiverId: '',
        content: '',
        isRead: true,
        createdAt: new Date().toISOString(),
      },
      unreadCount: 0,
      lastMessageAt: new Date().toISOString(),
    });
  }, [routePartnerId, inbox, user?.role]);

  const handleSelect = (item: InboxItem) => {
    setSelected(item);
    navigate(`${basePath}/${item.partnerId}`);
  };

  const activePartnerId = selected?.partnerId ?? routePartnerId;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Mesajlar</h1>
        <p className="mt-1 text-sm text-slate-500">Toptancı ve restoran iletişim merkezi</p>
      </div>

      <div className="grid h-[560px] max-h-[calc(100dvh-12rem)] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm lg:grid-cols-[320px_1fr]">
        <div className="flex h-full min-h-0 flex-col overflow-hidden border-b border-slate-200 lg:border-b-0 lg:border-r">
          <ChatInbox selectedPartnerId={activePartnerId} onSelect={handleSelect} />
        </div>

        <div className="flex h-full min-h-0 flex-col overflow-hidden">
          {activePartnerId ? (
            <ChatThread key={activePartnerId} partnerId={activePartnerId} partnerName={selected?.partner.name} />
          ) : (
            <Card className="m-4 flex flex-1 flex-col items-center justify-center gap-3 border-0 bg-slate-50 text-center shadow-none">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-slate-100 text-slate-400">
                <MessageSquare className="h-7 w-7" />
              </div>
              <div>
                <p className="font-medium text-slate-700">Bir konuşma seçin</p>
                <p className="mt-1 text-sm text-slate-500">
                  Sol taraftan bir konuşma seçerek mesajlaşmaya başlayın
                </p>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
